/* Серверный режим экранов «Фото» и «Видео» на движке шаблона (план вехи 3, шаги 2–3; план фото/видео §2): createPhoto(createPhotoServer)
   (features/photo/engine.ts) отдаёт сюда крючки каждого вида (фото и видео — два экземпляра каркаса make), а каждая вставка SRV спрашивает
   этот объект. Правила:
   - одна привязка — один кадр ServerStudio; у кадра свои режимы (EngineBinding.modes, mode-group.tsx): каждый режим шаблона — своё
     приложение сервера со своим сервисом заказа, а у каждого сервиса свои мост загрузок, мост референса, блок «Съёмка», зеркало входов
     (ModeCtx). Шаблон меняет режим на месте («Из фото · По описанию», «Без стиля» ↔ стиль, фото ↔ ролик), S и разметка остаются,
     сервер переводит себя на сервис нового режима (route по виду шаблона, photo-inputs.ts); файл, согласие и заявка одного режима под
     другим не уходят: новый режим получает входы вида (transfer) и права только вместе с теми же файлами (Q-PV1);
   - движок не пишет в sessionStorage и сам ничего не создаёт: заявку, ключ, отправку, опрос и отказы ведёт сервис режима; цена на кнопке —
     для показа, в заказ уходит svc.derive().price (I3);
   - входы пишет одна точка — push() (syncFoot шаблона) через зеркало: только опубликованные поля и значения; ключи загрузок — мосты;
   - вход из повтора «Истории» и заявки родной формы переносится в S один раз, при первой форме режима (applyInputs); файлы повтора —
     превью сервера (фото), видео — загрузить заново (Q7-video);
   - без своего снимка нижний слой «Пример» — нейтральный силуэт, демо-лицо BC.YOU не показывается (D12, Q-PV4); пример «Было · Стало» —
     только constraints.<вид>.example сервера;
   - тексты — словарь профиля в момент показа (uiText, uiFormat), строки ядра — через ui.tx (BotCore.tx). */
import { endpoints } from '../../api';
import { uiFormat, uiText } from '../../i18n';
import type { SpeechOrder } from '../../api/speech';
import { circleBalance, forgetCircleBalance, markSeen, rememberCircleBalance } from '../studio/circle/tool-helpers';
import { tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { studioApi, studioAppUnit, studioCheapOfferAt, studioExpectedPrice, studioFields, studioUnitPrice, type StudioApp, type StudioForm } from '../studio/studio-api';
import { priceUnit, record, rows, safeMediaUrl, text } from '../studio/studio-content';
import { details, detailsKey, leaseSkins, loadDetails, skinUrl } from '../studio/engine-kit/char-cards';
import { studioMediaUrl } from '../studio/studio-preview-model';
import { uploadExample } from '../studio/studio-upload-model';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { createCamBridge, type CamBridge, type CamBlock } from '../studio/engine-kit/cam-bridge';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineModes, type EngineScreen } from '../studio/engine-kit/engine-host';
import { createInputsMirror, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { acceptString, createMediaBridge, mediaNote, mediaPolicy, type MediaBridge, type MediaItem, type MediaPolicy } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { createResultGallery, PHOTO_LIMITS, type ResultGallery } from '../studio/engine-kit/result-gallery';
import { handoffPhoto, handoffVideo } from '../studio/engine-kit/result-handoff';
import { createResultMedia, type ResultMedia } from '../studio/engine-kit/result-media';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { castRef, parseRef, peopleModule, refKey, SILHOUETTE, type SelItem } from '../studio/engine-kit/people-bridge';
import { createRefBridge, type RefBridge, type RefLike } from '../studio/engine-kit/ref-bridge';
import { screenDoc, type ScreenDoc } from '../studio/engine-kit/screen-doc';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { photoMissing, photoPolicy, photoThemeOrder, photoThemes } from './photo-model';
import { videoMissing, videoPhotoPolicy, videoPolicy } from '../video/video-model';
import { videoActionId, videoResultTarget } from '../video/video-result-target';
import {
  TRAY_KEYS,
  photoInputs,
  photoView,
  routeMode,
  serverMode,
  sourceFormat,
  templateEntry,
  type PhotoKind,
  type PhotoView,
  type ServerMode,
} from './photo-inputs';
import { PROMPT_MAX } from '../studio/prompt-limit';

export type { PhotoKind } from './photo-inputs';

/* ---------- контракт с движком (features/photo/engine.ts, serve(вид, {...})) ---------- */

/** Фото формы шаблона (BC.files.image): адрес, размеры, кадр */
export interface PhotoFile {
  name: string;
  size: number;
  url: string;
  w: number;
  h: number;
  crop: { cx: number; cy: number; z: number } | null;
  f?: string;
  kind?: string;
  demo?: boolean;
}
/** Ролик формы шаблона (BC.files.clip): обложка, размеры, длина, окно */
export interface PhotoClip {
  name: string;
  size: number;
  url: string;
  video?: string;
  poster: string | null;
  w: number;
  h: number;
  dur: number;
  trim?: { from: number; to: number };
}
/** Стиль ленты шаблона (STY bot-photo.js) — из стилей формы сервера */
export interface TemplateStyle {
  id: string;
  n: string;
  d: string;
  ch: null;
  f: string;
  th: string[];
  pop: number;
  isNew: boolean;
  b: string;
  look: string;
  /** Картинка карточки (photos[0] сервера, безопасный адрес) */
  img: string | null;
  /** Примеры карточки стиля (examples сервера) */
  ex: string[];
  /** Движение «Видео товара» */
  cov?: boolean;
  none?: boolean;
}
/** Состояние экрана шаблона (S) */
export interface PhotoState {
  mode: string;
  preset: string | null;
  app: { id: number; n: string; f: string; k: number } | null;
  sty: string | number;
  list: unknown[];
  file: PhotoFile | null;
  files: PhotoFile[];
  clip: PhotoClip | null;
  crop: unknown;
  text: string;
  open: boolean;
  ar: string;
  n: number;
  dur: number;
  cheap: boolean;
  sh: unknown;
  want: string;
  people: SelItem[];
  extra: unknown[];
  key?: string | null;
}
/** Профиль вида (KINDS bot-photo.js): то, что читает сервер */
export interface KindProfile {
  kind: PhotoKind;
  title: string;
  word: string;
  unit: string;
  arDef: string;
  modes: Array<[string, string, string]>;
  people: { max: number; acts: string[] };
  next: Array<[string, string, string, string]>;
  T: Record<string, unknown>;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса) */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  [key: string]: unknown;
}
interface EngineUpload {
  o: { mb?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
/** BotRef.make экрана (REF, REFC) */
interface EngineRef extends RefLike {
  busy(): boolean;
  miss(): string;
  snap(): Record<string, unknown> | null;
  restore(s: { files: unknown[] } | null): void;
  say(text: string, el?: Element | null): void;
  readonly isOn: boolean;
}
/** BotPeople.attach экрана (P) */
interface EnginePeople {
  inFrame(): boolean;
  setIn(on: boolean): void;
  count(): number;
  snap(): Record<string, unknown>;
  prune(): void;
  paintForm(): void;
  close(): void;
  readonly isOpen: boolean;
}
/** Крючки, которые движок отдаёт serve (engine.ts, блок SRV после S) */
export interface PhotoEngineHooks {
  K: KindProfile;
  S: PhotoState;
  NS: string;
  CLIP_MAX: number;
  readonly CAM: { st: string; sim: boolean };
  readonly VT: { on: boolean; close(): void };
  readonly P: EnginePeople;
  readonly REF: EngineRef;
  readonly REFC: EngineRef | null;
  readonly SHOOT: CamBlock & { reset(): void };
  readonly SC: { sync(): void; close(): void };
  readonly FOOT: EngineFoot;
  readonly tail: BotRunApi;
  readonly UP: EngineUpload;
  readonly RAIL: { paint(): void; html(): string };
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  src(): 'ph' | 'vd' | 'tx';
  mediaOn(): boolean;
  trayOn(): boolean;
  coverOn(): boolean;
  refOn(): boolean;
  refAct(): boolean;
  refNow(): EngineRef | null;
  pplOn(): boolean;
  noStyle(): boolean;
  noneOk(): boolean;
  fixed(): unknown;
  pre(): Record<string, unknown> | null;
  sel(): TemplateStyle;
  styleById(id: unknown): TemplateStyle;
  arOf(): string | null;
  durOf(): number;
  needText(): boolean;
  txKind(): string;
  priceFrom(): boolean;
  setup(o: unknown): void;
  setLimits(price: number, min: number, max: number): void;
  paintPage(): void;
  renderBody(anim: boolean): void;
  fileChanged(): void;
  paintUp(): void;
  syncText(): void;
  subSync(): void;
  railShow(): void;
  heroRepaint(): void;
  putPhoto(f: PhotoFile): void;
  putClip(c: PhotoClip): void;
  trayFix(): void;
  coverPic(s: TemplateStyle, o: Record<string, unknown>): string;
  camStop(): void;
  sheetClose(): void;
  stepsAs(x: Record<string, unknown>): string[][];
}
type Missing = '' | 'load' | 'off' | 'blocked' | 'unavailable' | 'upload';
type Ready = '' | 'upload' | 'upfail' | 'rule';
type Cap = 'none' | 'reference' | 'shoot' | 'people' | 'improve';

export interface PhotoServer {
  ui<U extends object>(base: U): ServerUi<U>;
  entry(o: unknown): Record<string, unknown>;
  open(o: Record<string, unknown>): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: string, value: T, extra?: unknown): T;
  has(cap: Cap): boolean;
  peopleMax(): number;
  styles(): TemplateStyle[];
  themes(): string[];
  pic(s: TemplateStyle | null, add?: string, cls?: string): string;
  fileUrl(): string;
  sample(): string;
  example(): Record<string, unknown> | null;
  examples(s: TemplateStyle): string[];
  styleImg(s: TemplateStyle): string | null;
  title(): string;
  price(units: number, cheap: boolean): number;
  unit(): string;
  missing(): Missing | null;
  ready(): Ready;
  missText(kind: string): string;
  label(): string | null;
  upOpts(w: 'photo' | 'media' | 'tray'): Record<string, unknown>;
  refOpts(): Record<string, unknown>;
  pick(file: File): void;
  pickTray(files: File[], at?: number): void;
  remove(w: 'photo' | 'media' | 'tray', i?: number): boolean;
  recFailed(): void;
  camFailed(): void;
  push(): void;
  mode(m: string, btn: HTMLElement): boolean;
  styled(id: unknown): void;
  start(btn: HTMLElement): void;
  steps(): string[][] | null;
  snap(): Record<string, unknown>;
  resPic(sn: Record<string, unknown>, i: number): string;
  runPic(sn: Record<string, unknown>): string;
  click(d: DOMStringMap, el: HTMLElement): boolean;
  play(btn: HTMLElement): boolean;
  download(i: number): Promise<{ data: Blob; name: string }>;
  next(a: string, btn: HTMLElement): void;
  leave(go: () => void, focus: boolean): void;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest?: string): string };
  cNum(n: number): string;
  files: {
    image(file: File, cb: (f: { name: string; size: number; url: string; w: number; h: number }) => void, o?: { url?: string }): void;
    clip(file: File, cb: (c: PhotoClip) => void, o?: { url?: string; onUpdate?(c: PhotoClip): void }): void;
  };
  trim: { of(clip: PhotoClip, max: number): { from: number; to: number } };
  shake(el: Element, buzz?: unknown): void;
  offBuzz?: unknown;
}
const core = () => window.BotCore as unknown as Core;
const MB = 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const TRANSPORT = uploadFailureText(null);
/** Смена страницы SteosUI.swap идёт 170 мс: закрыть или сменить заказ BotRun раньше — нельзя (см. фасад хвоста) */
const SWAP_GUARD_MS = 400;
const RIGHTS = 'Подтвердите права на материалы';
const SWITCH_FAILED = 'Не удалось открыть режим. Попробуйте ещё раз.';
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
/** Пустой референс: экрану без референса нечего писать во входы */
const NO_REF: RefLike = { active: () => false, order: () => [] };
const isVideoFile = (file: File) => /^video\//.test(file.type || '') || /\.(mp4|m4v|mov|qt|webm)$/i.test(file.name);
const amountOf = (price: unknown): number | null => {
  const value = typeof price === 'number' ? price : record(price).amount ?? record(price).circle;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
};
/** Цена единицы на экране: платная форма — прайс сервера (фото — за фото, видео — за секунду), иначе карточка каталога */
const cardUnit = (c: { app: StudioApp }, form: StudioForm | null | undefined) =>
  studioAppUnit(amountOf(c.app.price), form, form?.unit_price_per === 'second' ? 'second' : undefined);
/** Режим инструмента документа экрана: {mode, app_id, title} */
interface Tool {
  mode: ServerMode;
  app_id: number;
  title: string;
}
function toolsOf(doc: Record<string, unknown> | undefined, kind: PhotoKind): Tool[] {
  return rows(record(doc?.[kind]).tools).flatMap(tool => {
    const id = Number(tool.app_id);
    return Number.isSafeInteger(id) && id > 0 && typeof tool.mode === 'string' && text(tool.title)
      ? [{ mode: tool.mode as ServerMode, app_id: id, title: text(tool.title) }]
      : [];
  });
}
/** «Занята» кнопка шаблона (SteosUI.busy) */
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = (window as unknown as { SteosUI?: { busy?(btn: HTMLElement, fn: () => Promise<T>): Promise<T | undefined> } }).SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}

/** Состояние сервиса одного режима кадра */
interface ModeCtx {
  svc: OrderService;
  app: StudioApp;
  /** open — режим кадра при открытии (его входы — повтор или заявка — переносятся в S); switch — режим, в который перешли на месте (S главнее) */
  origin: 'open' | 'switch';
  media: MediaBridge;
  ref: RefBridge;
  cam: CamBridge;
  mirror: InputsMirror;
  /** Форма, для которой прошла сверка */
  form: StudioForm | null;
  /** Ошибка формы, уже сказанная в рамке */
  said: string;
  /** Записи моста, которые показывает рамка: ключ загрузки → запись */
  shown: Map<string, MediaItem>;
  /** Сколько превью повтора ещё читается */
  restoring: number;
  /** Кадр и окно повтора — для файла, который придёт превью */
  pendingCrop: PhotoFile['crop'];
  pendingTrim: { from: number; to: number } | null;
  /** inputs.shoot повтора — после словарей «Съёмки» */
  pendingShoot: string | undefined;
  /** Последний вид, записанный во входы (откат неудачной смены режима) */
  view: Pick<PhotoState, 'mode' | 'sty' | 'want'> | null;
  consent: ConsentSheet | null;
  /** Ход хвоста BotRun по сервису режима */
  driver: RunDriver;
  /** Подсмотренная при открытии заявка (до проверки владельца сервисом) */
  peek: Intent | null;
  /** Кадры итога «Фото» (owner-protected файлы итога) */
  gallery: ResultGallery;
  /** Ролик итога «Видео»: проигрывание в рамке и «Скачать» */
  result: ResultMedia;
  /** Идёт передача итога в другой экран */
  moving: boolean;
  /** Фото лотка «Видео товара» по записям моста */
  trayFiles: Map<MediaItem, PhotoFile>;
}
/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  modes: EngineModes | null;
  ctxs: Map<OrderService, ModeCtx>;
  doc: ScreenDoc<Record<string, unknown>>;
  /** Форма инструмента «В новом стиле»: лента стилей режимов без своего набора (edit у фото, photo и edit у видео) */
  styleForm: StudioForm | null;
  styleAsk: number;
  /** Идёт смена режима: id приложения, в которое переходим */
  switching: number | null;
  /** Вход шаблона, с которым экран настроен (setup) */
  entryUsed: string;
  layout: string;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  waiters: Array<() => void>;
}

const instances: Partial<Record<PhotoKind, { bind(b: EngineBinding): EngineBound }>> = {};

export function createPhotoServer(kind: PhotoKind, h: PhotoEngineHooks): PhotoServer {
  const { S, K } = h;
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относятся S и экземпляр хвоста */
  let painted: Frame | null = null;
  /** has('shoot') спрашивает блок «Съёмка», а тот — ключ экрана (shootScr → has('shoot')): вложенный вопрос — ответ шаблона */
  let shootAsk = false;

  const cur = () => (bound && bound === frame ? frame : null);
  const onForm = () => h.mounted() && !h.tail.run;
  /** Режим кадра сейчас: приложение и сервис (у кадра без режимов — его собственные) */
  function ctxOf(f: Frame): ModeCtx {
    const now = f.modes?.current() ?? { app: f.app, svc: f.svc };
    let c = f.ctxs.get(now.svc);
    if (!c) {
      c = makeCtx(f, now.svc, now.app, f.ctxs.size ? 'switch' : 'open');
      f.ctxs.set(now.svc, c);
    }
    return c;
  }
  const ctx = () => {
    const f = cur();
    return f ? ctxOf(f) : null;
  };
  const live = (c: ModeCtx) => {
    const s = c.svc.get();
    return { s, d: c.svc.derive(s) };
  };
  const tools = (f: Frame | null) => (f ? toolsOf(f.doc.data(), kind) : []);
  const formOf = (c: ModeCtx | null) => c?.svc.get().form ?? null;
  /** Цена без скидки за units фото (секунд видео): расчёт заказа для текущего числа, иначе цена формы под него */
  const basePrice = (units: number) => {
    const c = ctx();
    if (!c) return 0;
    const s = c.svc.get();
    const form = s.form;
    if (!form) return (amountOf(c.app.price) ?? 0) * units;
    const key = kind === 'photo' ? 'count' : 'duration';
    const value = String(kind === 'photo' ? units : units > 5 ? 10 : 5);
    const now = kind === 'photo' ? String(S.n) : String(h.durOf() > 5 ? 10 : 5);
    /* прайс сервера за фото или секунду: оценка любого варианта, пока нет расчёта; у «посекундных» цена формы — 0 (сумма неизвестна
       до длительности), и без этого кнопка и лист «Длительность» показывали «0 C» */
    const listed = studioUnitPrice(form, kind === 'photo' ? 'variant' : 'second');
    if (value === now) {
      const d = c.svc.derive();
      return d.quoted || listed === null ? d.price : listed * Number(value);
    }
    if (listed !== null) return listed * Number(value);
    const field = studioFields(form).find(item => item.key === key);
    return field ? studioExpectedPrice(form, { ...s.inputs, [key]: value }) : c.svc.derive().price;
  };
  /** Режим сервера сейчас: по форме, иначе по документу экрана, иначе по входу приложения */
  function modeNow(f: Frame, c: ModeCtx): ServerMode | null {
    return serverMode(kind, formOf(c)) ?? tools(f).find(tool => tool.app_id === c.app.id)?.mode ?? null;
  }
  /** Шаблон каталога: приложение экрана, которого нет среди режимов документа */
  const isTemplate = (f: Frame, c: ModeCtx) => {
    const list = tools(f);
    return list.length > 0 && !list.some(tool => tool.app_id === c.app.id);
  };
  const policyOf = (form: StudioForm | null) =>
    form ? (kind === 'photo' ? photoPolicy(form) : videoPhotoPolicy(form)) : null;
  const tip = (el: Element | null, value: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (value && at) h.ui().tip(at, uiText(value));
  };
  /** Причина, почему форма заперта (как lockedText моста загрузок) */
  function lockText(c: ModeCtx): string {
    const { s, d } = live(c);
    if (d.submissionBlocked) return s.integrityError || 'Проверяем доступность';
    if (s.intent) return 'Прежний заказ ещё не завершён. Сначала проверьте его результат.';
    return s.uploadingFile || c.media.pending() ? 'Загружаем файл' : 'Создаём';
  }

  /* ---- вид шаблона ---- */
  function viewOf(): PhotoView {
    const clip = h.src() === 'vd' ? S.clip : null;
    const main = h.trayOn() ? (S.files[0] ?? null) : S.file;
    return {
      mode: S.mode,
      src: h.src(),
      preset: S.preset,
      template: !!S.app,
      sty: String(S.sty),
      none: h.noStyle(),
      refAct: h.refAct(),
      text: S.text,
      ar: h.arOf(),
      n: S.n,
      dur: h.durOf(),
      crop: main?.crop ?? null,
      trim: clip && clip.dur > h.CLIP_MAX ? core().trim.of(clip, h.CLIP_MAX) : null,
    };
  }
  /** Люди в кадре по порядку («По описанию»): «Вы» (если в кадре и готов), затем близкие и персонажи ряда */
  function castOf(): Array<{ ref: string; action: string }> | null {
    if (!h.pplOn()) return null;
    const m = peopleModule();
    const out: Array<{ ref: string; action: string }> = [];
    /* выбранный в ряду без действия — act: null (actOf BotPeople) */
    const add = (ref: string | null, action: string | null = '') => {
      if (ref && !out.some(item => item.ref === ref)) out.push({ ref, action: (action ?? '').trim() });
    };
    if (h.P.inFrame()) add(castRef('me', m));
    for (const x of S.people) {
      const act = m?.actOf ? m.actOf(x) : x.act === 'own' ? (x.own ?? '') : (x.act ?? '');
      const ref = castRef(x.id, m);
      const skin = ref ? skins.get(charOf(ref) ?? 0) : undefined;
      add(ref && skin ? `${ref}:skin:${skin}` : ref, act);
    }
    return out;
  }
  /* ---- «Вид персонажа» (владелец 02.11.2026: вид — везде, где персонаж из библиотеки; как у «Рилса с вами») ---- */
  /** Вид персонажа в кадре: id персонажа → id вида карточки; уходит в cast_N строкой character:<id>:skin:<id> (картинку вида отдаёт Voice) */
  const skins = new Map<number, number>();
  const charOf = (ref: string) => (/^character:[1-9][0-9]*$/.test(ref) ? Number(ref.slice('character:'.length)) : null);
  /** Карточки, которые сейчас грузятся: один запрос и одна перерисовка на персонажа */
  const cardWait = new Set<number>();
  /** Слоты «Вид персонажа»: по одному на персонажа каталога в кадре (несколько — заголовок слота его имя); первым — обычный вид */
  function skinItems(): Array<Record<string, unknown>> {
    const ids = [...new Set((castOf() ?? []).flatMap(item => charOf(item.ref.split(':skin:')[0]) ?? []))];
    for (const id of [...skins.keys()]) if (!ids.includes(id)) skins.delete(id);
    const cards = ids.flatMap(id => {
      const card = details.get(detailsKey(id));
      if (card === undefined && !cardWait.has(id)) {
        cardWait.add(id);
        void loadDetails(id).finally(() => {
          cardWait.delete(id);
          h.SC.sync();
        });
      }
      return card ? [card] : [];
    });
    return cards.map(card => {
      const id = card.avatar.id;
      leaseSkins(card, () => h.SC.sync());
      const base = safeMediaUrl(card.avatar.image_thumb_url || card.avatar.image_url) ?? SILHOUETTE;
      const face = (url: string | null) => img(url || base);
      const chosen = skins.get(id);
      return {
        k: `skin:${id}`,
        t: cards.length > 1 ? card.avatar.name : uiText('Вид персонажа'),
        kind: 'faces',
        cur: chosen && card.avatar.skins.some(sk => sk.id === chosen) ? String(chosen) : 'def',
        opts: [['def', uiText('Обычный'), '', face(base)], ...card.avatar.skins.map(sk => [String(sk.id), sk.name, '', face(skinUrl(card, sk.id))])],
        set: (v: string) => {
          const skin = Number(v);
          if (Number.isSafeInteger(skin) && skin > 0) skins.set(id, skin);
          else skins.delete(id);
        },
      };
    });
  }
  /** Ключ загрузки того, что в рамке: ролик — video, фото — photo, лоток — слоты; пусто — null */
  function fileKey(): string | null {
    if (h.trayOn()) return S.files.length ? 'photo' : null;
    return S.clip ? 'video' : S.file ? 'photo' : null;
  }
  /** Входы для блока «Съёмка»: что в рамке шаблона (ролик — строки ролика), даже пока его загрузка идёт. Как ключ экрана шаблона
      (shootScr: src() 'vd' — «fl:clip»): ролик в рамке или пустая рамка ждёт ролик (S.want: вход vdvid, «Изменить видео», запись с камеры) */
  function camInputs(c: ModeCtx): Record<string, string> {
    const inputs = { ...c.svc.get().inputs };
    if (h.src() === 'vd') inputs.video ||= 'pending';
    else delete inputs.video;
    return inputs;
  }

  /* ---- стили и картинки ---- */
  function serverStyles(form: StudioForm | null): TemplateStyle[] {
    if (!form) return [];
    if (kind === 'video' && serverMode(kind, form) === 'product') {
      return rows(record(form.constraints?.video).motions).flatMap(m =>
        typeof m.id === 'string' || typeof m.id === 'number'
          ? [
              {
                id: String(m.id),
                n: text(m.name),
                d: text(m.description).replace(/\.$/, ''),
                ch: null,
                f: '',
                th: [],
                pop: Number(m.popularity) || 0,
                isNew: m.new === true,
                b: m.hit === true ? uiText('Хит') : m.new === true ? uiText('Новое') : '',
                look: 'photo',
                img: studioMediaUrl(m.preview_url ?? m.preview) ?? null,
                ex: [],
                cov: true,
              },
            ]
          : [],
      );
    }
    const policy = policyOf(form);
    return (policy?.styles ?? []).map(s => ({
      id: s.id,
      n: s.name,
      d: s.description.replace(/\.$/, ''),
      ch: null,
      f: '',
      th: s.themes.slice(),
      pop: s.popularity,
      isNew: s.isNew,
      /* метка карточки — как у родной ленты (PhotoStyles): «Хит», иначе «Новое» */
      b: s.hit ? uiText('Хит') : s.isNew ? uiText('Новое') : '',
      look: s.look === 'drawn' ? 'drawn' : 'photo',
      img: studioMediaUrl(s.photos[0]) ?? null,
      ex: s.examples.map(url => studioMediaUrl(url)).filter((url): url is string => !!url),
    }));
  }
  function styles(): TemplateStyle[] {
    const f = cur(),
      c = f ? ctxOf(f) : null;
    const own = serverStyles(formOf(c));
    if (own.length || !f || !c) return own;
    const mode = modeNow(f, c);
    /* режимы без своего набора (edit у фото; photo и edit у видео): лента — набор инструмента «В новом стиле» */
    if ((kind === 'photo' && mode === 'edit') || (kind === 'video' && (mode === 'photo' || mode === 'edit'))) return serverStyles(f.styleForm);
    return own;
  }
  /** Форма «В новом стиле» для ленты режимов без своего набора — один раз на кадр, когда режимы известны */
  function loadStyleForm(f: Frame) {
    const tool = tools(f).find(item => item.mode === 'style');
    if (!tool || f.styleForm?.app_id === tool.app_id || f.styleAsk === tool.app_id) return;
    f.styleAsk = tool.app_id;
    void studioApi
      .form(tool.app_id)
      .then(form => {
        if (frame !== f || form.app_id !== tool.app_id) return;
        f.styleForm = form;
        changed(f);
      })
      .catch(() => {
        if (f.styleAsk === tool.app_id) f.styleAsk = 0;
      });
  }
  /** Строка шаблона словарём профиля: числа внутри — подстановкой ({0}), чтобы «5 сек», «2 фото» и «до 10 сек» были одним ключом на все числа */
  function tr(value: unknown): unknown {
    if (typeof value !== 'string' || !value) return value;
    const numbers: string[] = [];
    const template = value.replace(/\d+/g, n => {
      numbers.push(n);
      return `{${numbers.length - 1}}`;
    });
    return numbers.length && template !== value && uiText(value) === value ? uiFormat(template, ...numbers) : uiText(value);
  }
  /** Слоты «Ещё настройки» (setItems шаблона): заголовки, значения, подсказки, замки, варианты и чипы — словарём профиля.
      Свои части BotCam («Съёмка») и BotRef («Свой референс») — как есть: у них своего шва нет */
  function itemsT(list: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    /* ячейка «Свой референс» «Видео товара» (REFC.slot) — только если форма референс публикует */
    return [...list.filter(it => it.k !== 'ref' || server.has('reference')), ...skinItems()].map(it => {
      if (!['ar', 'n', 'dur', 'snd', 'tx'].includes(String(it.k))) return it;
      const out: Record<string, unknown> = { ...it };
      for (const key of ['t', 'v', 'lock', 'tip', 'ph', 'd']) if (typeof out[key] === 'string') out[key] = tr(out[key]);
      if (Array.isArray(it.opts)) out.opts = optsT(it.opts as unknown[][]);
      if (Array.isArray(it.chips)) out.chips = (it.chips as Array<Record<string, unknown>>).map(c => ({ ...c, l: tr(c.l) }));
      return out;
    });
  }
  /** Варианты меню [значение, подпись, пояснение] — подпись и пояснение словарём профиля (цена — разметка, как есть) */
  const optsT = (opts: unknown[][]) =>
    opts.map(([v, l, d, ...rest]) => [v, tr(l), typeof d === 'string' && !d.includes('<') ? tr(d) : d, ...rest]);
  const img = (url: string, cls?: string) =>
    `<img${cls ? ` class="${cls}"` : ''} src="${h.esc(url)}" alt="" draggable="false">`;

  /* ---- готовность ---- */
  /** Загруженные файлы (повтор, смена режима без переноса прав) без согласия: кнопка «Подтвердите права на материалы» открывает лист.
      Без файла согласие спросит мост загрузок при выборе файла */
  function rightsNeeded(c: ModeCtx): boolean {
    const { s } = live(c);
    if (!s.form || s.rights) return false;
    return studioFields(s.form).some(
      field => !!field.upload && !!(s.inputs[field.key] || s.uploadIds[field.key]) && !(field.key === 'reference' && s.inputs.style && s.inputs.style !== 'none'),
    );
  }
  /** Что починить вместо создания: форма не загрузилась или устарела — перечитать; загрузка не подтверждена — продолжить тем же id;
      файлы повтора без согласия — лист прав */
  function recovery(c: ModeCtx): ((btn: HTMLElement) => void) | null {
    const { s } = live(c);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => c.svc.setters.revision(value => value + 1);
    if (c.media.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => c.media.resume());
    if (rightsNeeded(c)) return () => askRights(c);
    return null;
  }
  function askRights(c: ModeCtx) {
    c.consent?.close();
    c.consent = openConsentSheet({
      rights: () => c.svc.get().rights,
      locked: () => c.svc.derive().locked,
      setRights: on => c.svc.setters.rights(on),
      confirm: true,
      onContinue: () => {
        c.consent = null;
        h.FOOT.sync();
      },
      onClose: () => {
        c.consent = null;
      },
    });
  }
  let rule = '';
  /** Пределы описания формы режима (подсказки «Добавьте описание, от N знаков», «Описание длиннее N знаков») */
  const limit = { min: 10, max: PROMPT_MAX };
  function missing(): Missing | null {
    const f = cur();
    if (!f) return 'load';
    const c = ctxOf(f);
    if (f.switching) return 'load';
    const { s, d } = live(c);
    if (s.intent && !d.orderId) return '';
    if (!s.identity && !s.integrityError) return 'load';
    if (s.integrityError || !s.identity || s.identity.appId !== c.app.id) return 'blocked';
    if (!s.form) return s.error ? '' : 'load';
    if (s.versionStale) return '';
    if (!(kind === 'photo' ? photoPolicy(s.form) : videoPolicy(s.form))) return 'off';
    if (!s.form.available) return 'unavailable';
    if (c.restoring) return 'upload';
    return null;
  }
  function ready(): Ready {
    const c = ctx();
    if (!c) return 'upload';
    const { s } = live(c);
    if (c.media.pending()) return c.media.stalled() && s.error === TRANSPORT && !s.busy ? '' : 'upload';
    if (rightsNeeded(c)) return '';
    const key = fileKey();
    if (key && !s.inputs[key]) return 'upfail';
    if (!s.form) return '';
    const why = kind === 'photo' ? photoMissing(s.form, s.inputs, s.rights, s.uploadIds) : videoMissing(s.form, s.inputs, s.rights, s.uploadIds);
    if (why && why !== RIGHTS) {
      rule = why;
      return 'rule';
    }
    /* в заказ уходит цена сервиса (расчёт сервера под состав); пока она расходится с ценой на кнопке, создание закрыто — иначе заказ
       ушёл бы с ценой формы (у «посекундных» 0) и получил бы 409 price_changed */
    if (s.form.chargeable !== false && server.price(kind === 'photo' ? S.n : h.durOf() > 5 ? 10 : 5, false) !== live(c).d.price) {
      rule = 'Цена этого выбора пока не подтверждена';
      return 'rule';
    }
    return '';
  }

  /* ---- вход шаблона и перенос входов в S ---- */
  function entryFor(f: Frame, c: ModeCtx): Record<string, unknown> {
    const init = f.b.initialInputs ?? {};
    const options = c.app.entry ?? {};
    const mode =
      modeNow(f, c) ??
      ((): ServerMode | null => {
        const preset = text(options.preset) || text(init.preset);
        const m = text(options.mode) || text(init.mode);
        if (kind === 'photo')
          return preset === 'avatar' ? 'avatar' : preset === 'restore' ? 'restore' : preset === 'color' || preset === 'colorize' ? 'colorize' : m === 'tx' ? 'generate' : null;
        return preset === 'cover' ? 'product' : preset === 'parallax' ? 'parallax' : m === 'tx' ? 'text' : m === 'ph' ? 'photo' : m === 'vd' ? 'edit' : null;
      })();
    const o: Record<string, unknown> = isTemplate(f, c)
      ? { app: { id: c.app.id, n: c.app.name, f: text(c.app.format), k: Math.max(1, serverStyles(formOf(c)).length) } }
      : templateEntry(kind, mode);
    const said = typeof init.text === 'string' ? init.text : typeof init.description === 'string' ? init.description : '';
    if (said.trim()) o.text = said;
    return o;
  }
  /** Входы повтора или заявки → S: только то, что пришло во входах (остальное — как было); файлы — превью сервера */
  function applyInputs(f: Frame, c: ModeCtx, form: StudioForm, inputs: Record<string, string>) {
    const patch = photoView(kind, form, inputs);
    if (patch.sty !== undefined) {
      S.sty = patch.sty === 'none' && h.noneOk() ? 'none' : (styles().find(s => s.id === patch.sty)?.id ?? S.sty);
      S.list = [];
    }
    if (patch.text !== undefined) S.text = patch.text;
    if (patch.ar !== undefined) S.ar = patch.ar;
    if (patch.n !== undefined) S.n = patch.n;
    if (patch.dur !== undefined) S.dur = patch.dur;
    c.pendingCrop = patch.crop ?? null;
    c.pendingTrim = patch.trim ?? null;
    if (patch.cast) {
      const m = peopleModule();
      const acts = K.people.acts;
      const sel: SelItem[] = [];
      let me = false;
      skins.clear();
      for (const cast of patch.cast) {
        /* вид персонажа (character:<id>:skin:<id>) — в skins, состав — по ссылке персонажа */
        const [base, skinRaw] = cast.ref.split(':skin:');
        const skin = Number(skinRaw);
        if (skinRaw && Number.isSafeInteger(skin) && skin > 0 && charOf(base)) skins.set(charOf(base)!, skin);
        const item = { ...cast, ref: base };
        const key = refKey(item.ref);
        if (key === null) continue;
        if (key === 'me') {
          me = true;
          continue;
        }
        if (!parseRef(item.ref) || sel.some(x => x.id === key)) continue;
        const action = item.action.trim();
        sel.push({ id: key, ...(action ? (acts.includes(action) ? { act: action } : { act: 'own', own: action }) : {}), role: null });
      }
      S.people = sel;
      if (me && m) h.P.setIn(true);
    }
    if (inputs.video && kind === 'video') S.want = 'vd';
    c.pendingShoot = inputs.shoot;
    c.cam.restore(inputs.shoot);
    restoreFiles(c, inputs);
    void c.ref.restore(inputs).then(result => {
      if (!result || frame !== f || ctxOf(f) !== c) return;
      const R = h.refNow();
      if (R && result.files.length) R.restore({ files: result.files });
      if (result.text) h.UP.say(uiText(result.text));
    });
  }
  /** Файлы входов: превью загрузок (фото), видео — загрузить заново (превью сервер не отдаёт, Q7-video) */
  function restoreFiles(c: ModeCtx, inputs: Record<string, string>) {
    const track = (job: Promise<void>) => {
      c.restoring++;
      void job.finally(() => {
        c.restoring = Math.max(0, c.restoring - 1);
        if (cur() && ctxOf(cur()!) === c && h.mounted()) h.FOOT.sync();
      });
    };
    if (h.trayOn()) {
      if (TRAY_KEYS.some(key => inputs[key])) track(c.media.slots(trayKeys(c)).restore());
      return;
    }
    if (inputs.video && kind === 'video') track(c.media.restore('video', inputs.video));
    else if (inputs.photo) track(c.media.restore('photo', inputs.photo));
  }
  const trayKeys = (c: ModeCtx) => TRAY_KEYS.filter(key => studioFields(formOf(c)).some(field => field.key === key));

  /* ---- мост загрузок: файлы в рамке шаблона ---- */
  function putItem(c: ModeCtx, item: MediaItem, trayName: string) {
    const file = item.file;
    if (!file) return;
    const tray = trayName === trayKeys(c).join(',') && trayKeys(c).length > 1;
    if (tray) {
      core().files.image(
        file,
        r => {
          const list = c.media.slots(trayKeys(c)).list();
          const at = list.indexOf(item);
          if (at < 0 || !onForm()) return;
          c.trayFiles.set(item, { ...r, crop: at === 0 ? c.pendingCrop : null, kind: 'photo' });
          if (at === 0) c.pendingCrop = null;
          trayPaint(c);
          h.ui().buzz();
        },
        { url: item.url },
      );
      return;
    }
    c.shown.set(item.key, item);
    if (item.kind === 'video') {
      /* ролик заменяет фото в рамке: загрузка фото снимается */
      if (c.shown.has('photo') || c.svc.get().inputs.photo) dropKey(c, 'photo');
      core().files.clip(
        file,
        clip => {
          if (c.shown.get('video') !== item) return;
          if (c.pendingTrim) clip.trim = c.pendingTrim;
          c.pendingTrim = null;
          h.putClip(clip);
          h.ui().buzz();
        },
        {
          url: item.url,
          onUpdate: clip => {
            if (S.clip === clip && onForm() && h.src() === 'vd') h.fileChanged();
          },
        },
      );
      return;
    }
    if (kind === 'video' && (c.shown.has('video') || c.svc.get().inputs.video)) dropKey(c, 'video');
    core().files.image(
      file,
      r => {
        if (c.shown.get('photo') !== item) return;
        const crop = c.pendingCrop;
        c.pendingCrop = null;
        h.putPhoto({ ...r, crop, kind: 'photo' });
        h.ui().buzz();
      },
      { url: item.url },
    );
  }
  /** Убрать загрузку ключа (файл другого вида встал в рамку): запись моста или ключ входов */
  function dropKey(c: ModeCtx, key: string) {
    c.shown.delete(key);
    c.media.remove(key);
  }
  /** Лоток «Видео товара» по записям моста: фото по порядку слотов (главное — первое) */
  function trayPaint(c: ModeCtx) {
    const list = c.media.slots(trayKeys(c)).list();
    for (const item of c.trayFiles.keys()) if (!list.includes(item)) c.trayFiles.delete(item);
    S.files = list.map(item => c.trayFiles.get(item)).filter((file): file is PhotoFile => !!file);
    h.trayFix();
    h.fileChanged();
  }
  function dropItem(c: ModeCtx, item: MediaItem | null, key: string) {
    if (h.trayOn()) {
      trayPaint(c);
      return;
    }
    const shown = c.shown.get(key);
    if (item && shown !== item) return;
    c.shown.delete(key);
    if (key === 'video') S.clip = null;
    else S.file = null;
    h.fileChanged();
  }

  /* ---- смена режима ---- */
  /** Приложение режима, которого ждёт вид шаблона (или null — режим тот же / неизвестен) */
  function wanted(f: Frame, c: ModeCtx, v: PhotoView): number | null {
    if (!f.modes) return null;
    const want = routeMode(kind, v);
    if (!want || modeNow(f, c) === want || isTemplate(f, c)) return null;
    const tool = tools(f).find(item => item.mode === want);
    return tool && tool.app_id !== c.app.id ? tool.app_id : null;
  }
  async function switchTo(f: Frame, c: ModeCtx, target: number): Promise<boolean> {
    f.switching = target;
    h.FOOT.sync();
    const { s } = live(c);
    const seed = { ...s.inputs };
    const carried = Object.keys(seed).some(key => studioFields(s.form).some(field => field.key === key && field.upload));
    const ok = !!f.modes && (await f.modes.switchTo(target, seed, { transfer: true, rights: carried && s.rights }).catch(() => false));
    if (frame !== f) return false;
    f.switching = null;
    if (!ok) {
      /* режим не сменился: шаблон — на прежний вид, причина в рамке */
      const last = c.view;
      if (last) {
        S.mode = last.mode;
        S.sty = last.sty;
        S.want = last.want;
        S.list = [];
      }
      if (onForm()) h.paintPage();
      h.UP.say(uiText(SWITCH_FAILED));
      return false;
    }
    changed(f);
    return true;
  }
  /** Вид шаблона требует другой режим: смена (пока мост грузит файл — после загрузки, push повторится) */
  function route(f: Frame, c: ModeCtx): boolean {
    if (f.switching || c.media.pending() || c.svc.derive().locked) return !!f.switching;
    const target = wanted(f, c, viewOf());
    if (target === null) return false;
    void switchTo(f, c, target);
    return true;
  }

  /* ---- S → входы ---- */
  function push() {
    const f = cur();
    if (!f || !onForm()) return;
    const c = ctxOf(f);
    if (route(f, c)) return;
    const form = formOf(c);
    if (!form) return;
    c.mirror.commit(prev => {
      let next = photoInputs(kind, form, viewOf(), prev, { cast: castOf(), shoot: c.cam.order() });
      /* ключи загрузок — по записям моста, если они есть (форма перечитана — id снова на месте); перенесённые сменой режима — как были */
      for (const keys of [trayKeys(c), ['photo'], ['video']]) {
        const tray = keys.length ? c.media.slots(keys) : null;
        if (tray?.list().length) next = tray.inputsFor(next);
      }
      if (c.ref.has()) next = c.ref.inputsFor(next, h.refNow() ?? NO_REF);
      return next;
    });
    c.view = { mode: S.mode, sty: S.sty, want: S.want };
  }

  /* ---- сверка формы с сервисом ---- */
  function limits(c: ModeCtx) {
    const form = formOf(c);
    if (!form) return;
    const policy = kind === 'photo' ? photoPolicy(form) : videoPolicy(form);
    const unit = cardUnit(c, form) ?? (kind === 'photo' ? form.expected_price : Math.round(form.expected_price / 5));
    const min = Number(record(policy).description_min_length ?? record(policy).description_min ?? 10);
    const max = Math.min(PROMPT_MAX, Number(record(policy).description_max_length ?? record(policy).description_max ?? PROMPT_MAX));
    limit.min = Number.isFinite(min) ? min : 10;
    limit.max = Number.isFinite(max) && max > 0 ? max : PROMPT_MAX;
    h.setLimits(unit, limit.min, limit.max);
    const sizes = ['photo', 'video', ...trayKeys(c)].map(key => mediaPolicy(form, key)?.maxBytes ?? 0).filter(Boolean);
    if (sizes.length) h.UP.o.mb = Math.max(...sizes) / MB;
  }
  function layoutOf(): string {
    const f = cur(),
      c = f ? ctxOf(f) : null;
    return JSON.stringify([
      [S.preset, !!S.app, server.title(), server.wrap('modes', K.modes).map(m => m[0])],
      [S.mode, server.has('none'), server.has('shoot'), server.has('people'), server.has('improve')],
      f && c ? modeNow(f, c) : null,
      [server.has('reference'), styles().map(s => s.id)],
    ]);
  }
  /** Перерисовать то, что сменилось с прошлой раскладки (layoutOf): шапка (пресет, заголовок, сегмент режимов) — вся страница;
      тело (режим шаблона, ячейки по возможностям) — renderBody шаблона; лента (карточка «Свой референс», набор стилей) — только блок
      ленты; приложение режима — только цена в шапке и подвал. Смена режима у шаблона перерисовывает тело (сегмент) или ничего
      («Без стиля»): шапка, рамка загрузки, подвал и лист остаются теми же узлами */
  function repaint(f: Frame, layout: string) {
    const was = f.layout ? (JSON.parse(f.layout) as unknown[]) : null,
      now = JSON.parse(layout) as unknown[];
    f.layout = layout;
    const same = (i: number) => !!was && JSON.stringify(was[i]) === JSON.stringify(now[i]);
    /* другой набор стилей (режим со своим набором, набор «В новом стиле» пришёл позже): лента собирается заново, как у шаблона при смене режима */
    if (!same(3)) S.list = [];
    if (!same(0)) h.paintPage();
    else if (!same(1)) h.renderBody(false);
    else if (!same(3)) railPaint();
    else if (!same(2)) h.subSync();
  }
  /** Блок ленты заново (другая лента — с карточкой «Свой референс» или без неё — или другой набор); без блока — тело целиком */
  function railPaint() {
    const box = h.fixed() ? null : (h.root()?.querySelector('#pfSty') ?? null);
    if (!box) {
      h.renderBody(false);
      return;
    }
    box.innerHTML = h.RAIL.html();
    h.SC.sync();
    h.subSync();
  }
  function formSync(f: Frame, c: ModeCtx) {
    const s = c.svc.get(),
      form = s.form;
    if (!onForm()) return;
    if (form && c.form !== form) {
      const first = c.form === null;
      c.form = form;
      limits(c);
      if (first && c.origin === 'open') {
        /* режим и стиль экрана — по форме режима (вход шаблона мог быть угадан до неё) */
        const o = entryFor(f, c);
        if (JSON.stringify(o) !== f.entryUsed) {
          f.entryUsed = JSON.stringify(o);
          h.setup(o);
        }
        if (c.mirror.external(s.inputs)) applyInputs(f, c, form, s.inputs);
        c.mirror.seen(s.inputs);
        f.layout = '';
      } else h.subSync();
    }
    /* форма режима, в который перешли, ещё не пришла: раскладка ждёт её (лента режима без формы пуста) */
    const layout = form || !f.layout ? layoutOf() : f.layout;
    if (layout !== f.layout) repaint(f, layout);
    const error = s.integrityError ? '' : s.error;
    if (error !== c.said) {
      c.said = error;
      if (FORM_SAY.includes(error)) h.UP.say(uiText(error));
    }
    if (form) push();
    h.FOOT.sync();
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    const c = ctxOf(f);
    if (tools(f).length) loadStyleForm(f);
    if (!f.switching) {
      formSync(f, c);
      c.media.sync();
      /* заявка без номера заказа (исход POST неизвестен): страница «Заказ не подтверждён» — когда форма режима пришла или не пришла,
         чтобы её текст (причина) не менялся у человека на глазах; до того подвал формы предлагает «Продолжить проверку» */
      const s = c.svc.get();
      if (s.form || s.error || !s.intent || c.svc.derive(s).orderId) c.driver.sync();
      c.consent?.refresh();
      balance(f, c);
    } else h.FOOT.sync();
    if ((c.svc.get().form || c.svc.get().error) && !f.doc.loading()) {
      const list = f.waiters.splice(0);
      list.forEach(done => done());
    }
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту) */
  function balance(f: Frame, c: ModeCtx) {
    const orderId = c.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || c.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.FOOT.sync();
      });
  }

  /* ---- привязка кадра ---- */
  function makeCtx(f: Frame, svc: OrderService, app: StudioApp, origin: ModeCtx['origin']): ModeCtx {
    const c = {
      svc,
      app,
      origin,
      form: null,
      said: '',
      shown: new Map(),
      restoring: 0,
      pendingCrop: null,
      pendingTrim: null,
      pendingShoot: undefined,
      view: null,
      consent: null,
      peek: null,
      moving: false,
      trayFiles: new Map(),
    } as unknown as ModeCtx;
    const mine = () => frame === f && ctxOf(f) === c;
    /** Отклик моста в разметку шаблона: сбой отрисовки не должен оборвать очередь загрузок моста (он зовёт отклики посреди своего цикла) */
    const safe = (fn: () => void) => {
      try {
        fn();
      } catch (failure) {
        console.error(failure);
      }
    };
    const policyFor = (key: string): MediaPolicy | null => mediaPolicy(svc.get().form, key);
    c.media = createMediaBridge({
      svc,
      owner: () => svc.get().identity?.owner,
      policy: policyFor,
      say: text => {
        if (mine()) h.UP.say(text);
      },
      put: (item, tray) => {
        if (mine()) safe(() => putItem(c, item, tray));
      },
      drop: (item, key) => {
        if (mine()) safe(() => dropItem(c, item, key));
      },
      update: () => {
        if (!mine() || !onForm()) return;
        safe(() => {
          push();
          h.FOOT.sync();
        });
      },
    });
    c.ref = createRefBridge({ media: c.media, svc, owner: () => svc.get().identity?.owner });
    c.cam = createCamBridge({ block: () => h.SHOOT, form: () => svc.get().form, inputs: () => camInputs(c) });
    c.mirror = createInputsMirror(svc);
    c.driver = createRunDriver({
      svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => mine() && bound === f && h.mounted(),
      localSteps: () => server.steps() ?? [],
      paintForm: () => h.paintPage(),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f, c),
    });
    c.gallery = createResultGallery(PHOTO_LIMITS);
    c.result = createResultMedia({ svc, ic: name => h.ic(name) });
    void c.cam.load().then(() => {
      if (!mine()) return;
      if (c.pendingShoot !== undefined) c.cam.restore(c.pendingShoot);
      changed(f);
    });
    return c;
  }
  function dispose(f: Frame) {
    for (const c of f.ctxs.values()) {
      c.media.cancel();
      c.ref.cancel();
      c.consent?.close();
      c.gallery.release();
      c.result.release();
    }
    f.doc.abort();
    f.waiters.splice(0).forEach(done => done());
  }
  function bind(b: EngineBinding): EngineBound {
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) dispose(frame);
      frame = makeFrame(b);
    } else {
      frame.b = b;
      frame.modes = b.modes ?? null;
    }
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      ready: () =>
        new Promise<void>(resolve => {
          const c = ctxOf(mine);
          if ((c.svc.get().form || c.svc.get().error) && !mine.doc.loading()) resolve();
          else mine.waiters.push(resolve);
        }),
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      modes: b.modes ?? null,
      ctxs: new Map(),
      styleForm: null,
      styleAsk: 0,
      switching: null,
      entryUsed: '',
      layout: '',
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      waiters: [],
    } as unknown as Frame;
    f.doc = screenDoc<Record<string, unknown>>(kind, {
      identity: `${kind}-tools`,
      remember: true,
      onChange: () => changed(f),
    });
    void f.doc.load();
    return f;
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр (painted); ход ведёт водитель сервиса текущего режима ---- */
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;
  /** Когда BotRun начал заказ («Создать», «Попробовать ещё раз»): его страница встаёт сменой SteosUI.swap через 170 мс и рисует RUN;
      закрыть итог (drop) или уйти к форме (exit) раньше — значит обнулить RUN под ждущей сменой (TypeError в таймере шаблона) */
  let startedAt = 0;
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });
  /** Заявка заказа режима: сервиса, а пока он не проверил владельца — подсмотренная при открытии (потом её решает сервис) */
  function intentOf(c: ModeCtx | null): Intent | null {
    const s = c?.svc.get();
    if (!c || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? c.peek : null);
  }
  /** Входы отправленного заказа (тело заявки) или null — заказа нет, вид берётся с формы */
  const sentInputs = (c: ModeCtx | null) => (intentOf(c)?.body.inputs ?? null) as Record<string, string> | null;
  /** Исходник заказа: описание, ролик или фото */
  function srcOf(f: Frame | null, c: ModeCtx | null, inputs: Record<string, string>): 'ph' | 'vd' | 'tx' {
    const mode = f && c ? modeNow(f, c) : null;
    if (mode === 'generate' || mode === 'text') return 'tx';
    return inputs.video ? 'vd' : 'ph';
  }
  /** Строки состава заказа по порядку слотов */
  const castIn = (inputs: Record<string, string>) =>
    Object.keys(inputs)
      .filter(key => /^cast_[1-9]$/.test(key))
      .sort()
      .map(key => inputs[key])
      .filter(Boolean);
  /** Этапы шаблона (решение Q1): по входу заказа, а без заказа — по форме; подписи — словарь профиля в момент показа */
  function steps(): string[][] {
    const f = cur(),
      c = f ? ctxOf(f) : null;
    const inputs = sentInputs(c);
    let x: Record<string, unknown>;
    if (inputs) {
      const mode = f && c ? modeNow(f, c) : null;
      const cast = castIn(inputs);
      const styled = mode === 'product' ? !!inputs.motion : !!inputs.style && inputs.style !== 'none';
      x = {
        src: srcOf(f, c, inputs),
        none: !inputs.reference && !styled,
        preset: S.preset,
        people: cast.length > 0,
        solo: cast.length > 0 && cast.every(ref => ref === 'self' || ref.startsWith('user_avatar:')),
        ref: !!inputs.reference,
      };
    } else {
      const people = h.pplOn() && h.P.count() > 0;
      x = { src: h.src(), none: h.noStyle(), preset: S.preset, people, solo: people && !S.people.length, ref: h.refAct() };
    }
    return h.stepsAs(x).map(([k, i, a, b]) => [k, i, uiText(a), uiText(b)]);
  }
  /** Снимок входа заказа (r.snap BotRun) в виде шаблона: из тела заявки, а без заказа — из S; файл в памяти — только если он и есть файл заказа */
  function snapOf(): Record<string, unknown> {
    const f = cur(),
      c = f ? ctxOf(f) : null;
    const s = c?.svc.get();
    const it = intentOf(c);
    const inputs = sentInputs(c);
    const mode = f && c ? modeNow(f, c) : null;
    const src = inputs ? srcOf(f, c, inputs) : h.src();
    const styleId = inputs ? (mode === 'product' ? inputs.motion : inputs.style) : String(S.sty);
    const ref = inputs ? !!inputs.reference : h.refAct();
    const none = !ref && (!styleId || styleId === 'none');
    const sty = ref
      ? { id: 'none', n: uiText('Свой референс'), d: '', none: true, ref: true }
      : none
        ? { ...(h.noneOk() ? h.styleById('none') : { id: 'none', n: uiText('Без стиля') }), none: true }
        : { ...h.styleById(styleId) };
    const same = (key: string) => !inputs || (!!inputs[key] && inputs[key] === s?.inputs[key]);
    const photo = src === 'ph' && S.file && same('photo') ? S.file : null;
    const clip = src === 'vd' && S.clip && same('video') ? S.clip : null;
    const file = photo ? { ...photo } : clip ? { url: clip.poster ?? '', w: clip.w, h: clip.h, name: clip.name } : null;
    const format = inputs?.format ?? s?.inputs.format;
    const ar = (format && /^\d+:\d+$/.test(format) ? format : null) ?? h.arOf() ?? (file?.w && file.h ? `${file.w}:${file.h}` : K.arDef);
    const span = inputs?.start_ms && inputs.end_ms ? Math.round((Number(inputs.end_ms) - Number(inputs.start_ms)) / 1000) : 0;
    const dur = kind !== 'video' ? 0 : src === 'vd' ? (clip ? h.durOf() : 0) || span || 5 : Number(inputs?.duration) || S.dur || 5;
    const fx = h.fixed() as Record<string, unknown> | null;
    return {
      mode: src,
      fx: fx ? { ...fx } : null,
      sty,
      file,
      text: inputs ? (inputs.description ?? '') : S.text.trim(),
      ppl: h.pplOn() && (!inputs || castIn(inputs).length) && h.P.count() ? h.P.snap() : null,
      n: kind === 'photo' ? doneImages(s?.order) || Number(inputs?.count) || S.n || 1 : 1,
      dur,
      ar,
      cheap: !!(it ? it.body.cheap : S.cheap),
      title: server.title() || uiText(K.title),
      lb: text(h.pre()?.styLb),
      ref: null,
      shoot: null,
    };
  }
  /** Сколько кадров итога у готового заказа фото (сетка итога — до 4) */
  const doneImages = (order: SpeechOrder | null | undefined) =>
    kind === 'photo' && order?.status === 'done' && order.result?.available
      ? Math.min(4, order.result.assets.filter(asset => asset.mime.startsWith('image/')).length)
      : 0;
  /** Ролик итога (video/mp4) готового заказа */
  const mp4Of = (order: SpeechOrder | null | undefined) =>
    order?.status === 'done' && order.result?.available ? order.result.assets.find(asset => asset.mime === 'video/mp4') : undefined;
  /** Картинка заказа: display.picture сервера или свой снимок заказа (обложка ролика); нет — пусто, без демо-лица */
  function pictureOf(sn: Record<string, unknown>): string {
    const order = ctx()?.svc.get().order;
    const own = record(sn.file).url;
    const url = studioMediaUrl(record(record(record(order).display).picture).url) ?? (typeof own === 'string' && own ? own : '');
    return url ? `<img src="${h.esc(url)}" alt="">` : '';
  }
  /** Заказ готов (каждая сверка): кадры итога фото — один набор на id:revision, затем герой итога заново */
  function onDone(f: Frame, c: ModeCtx) {
    const order = c.svc.get().order;
    if (kind !== 'photo' || !order || order.status !== 'done') return;
    void c.gallery.load(order).then(items => {
      if (!items || frame !== f || ctxOf(f) !== c) return;
      const r = h.tail.run;
      if (!r || r.st !== 'done') return;
      const n = Math.min(items.length, 4);
      if (n && r.snap.n !== n) r.snap.n = n;
      h.heroRepaint();
    });
  }
  const notes = (mode: unknown) => ({
    modH: mode === 'tx' ? 'Описание не прошло проверку' : mode === 'vd' ? 'Видео не прошло проверку' : 'Фото не прошло проверку',
    techH: kind === 'photo' ? 'Не получилось сделать фото' : 'Не получилось сделать видео',
    notMade: kind === 'photo' ? 'фото не создано' : 'видео не создано',
  });
  function failTextOf(r: BotRunRun): string | null {
    const c = ctx() ?? (frame ? ctxOf(frame) : null);
    const s = c?.svc.get();
    const order = s?.order ?? null;
    const list = tailSteps(order);
    return failText(r, {
      order,
      error: s ? s.integrityError || s.error : '',
      texts: notes(r.snap.mode),
      step: list.length ? list[tailCurrent(order, list)] : undefined,
    });
  }
  function receiptOf(r: BotRunRun): string {
    const c = ctx() ?? (frame ? ctxOf(frame) : null);
    const s = c?.svc.get();
    const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
    return receipt(r, s?.order ?? null, { cheap, where: kind === 'photo' ? 'на фото' : 'на видео' });
  }
  const many = (r: BotRunRun) => Number(r.snap.n) > 1;
  const AR_WORDS: Record<string, string> = { '1:1': 'Квадрат', '3:4': 'Портрет', '4:5': 'Пост', '9:16': 'Сторис', '16:9': 'Горизонтальное' };
  /** Карточка итога (card шаблона: runWhat и whoHTML) — стиль или правка, формат словом, длительность или число фото; кто в кадре */
  function cardOf(r: BotRunRun, q: (what: string, text: unknown) => string): string {
    const sn = r.snap as Record<string, unknown> & { sty?: Record<string, unknown>; fx?: Record<string, unknown> | null; ppl?: Record<string, unknown> | null };
    const sty = sn.sty ?? {};
    const style = sn.fx
      ? uiText(String(sn.fx.n ?? ''))
      : sty.none
        ? uiText(String(sty.n ?? ''))
        : uiFormat('{0} «{1}»', uiText(text(sn.lb) || 'Стиль'), String(sty.n ?? ''));
    const ar = String(sn.ar ?? '');
    const format = uiText(AR_WORDS[ar] ?? (sn.mode === 'vd' ? 'Как у видео' : 'Как у фото'));
    const amount = kind === 'video' ? uiFormat('{0} с', sn.dur) : Number(sn.n) > 1 ? uiFormat('{0} фото', sn.n) : '';
    const card = q([style, format, amount].filter(Boolean).join(' · '), sn.text);
    const p = sn.ppl;
    const names = p
      ? [
          ...(record(p.me).in ? [uiText('Вы')] : []),
          ...rows(p.people).map(x => text(x.n)),
          ...rows(p.chars).map(x => text(x.name)),
        ].filter(Boolean)
      : [];
    const who = names.length ? `<span class="sf">${h.ic('users')}<span>${h.esc(names.join(', '))}</span></span>` : '';
    return card.slice(0, -6) + who + '</div>';
  }
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    const T = (opts.T ?? {}) as Record<string, unknown>;
    inst = create({
      ...opts,
      drive: (r: BotRunRun) => {
        const c = ctx();
        if (c) void c.driver.drive(r);
      },
      failText: (r: BotRunRun) => failTextOf(r),
      receipt: (r: BotRunRun) => receiptOf(r),
      foot: chatFoot(
        serverFoot(
          {
            ...((opts.foot as Record<string, unknown>) ?? {}),
            mod: ['data-pr-edit', 'edit', (r: BotRunRun) => uiText(r.snap.mode === 'tx' ? 'Изменить описание' : r.snap.mode === 'vd' ? 'Заменить видео' : 'Заменить фото')],
          },
          'pf',
        ),
        'pf',
      ),
      card: (r: BotRunRun) => cardOf(r, (what, about) => (facade as unknown as { q(w: string, t: unknown): string }).q(what, about)),
      /* «Скачать» (выбранный вариант — «Скачать № N») и «Ещё вариант» по стандарту (res шаблона) — словарём профиля */
      acts: (r: BotRunRun) => {
        const res = (opts.res ?? {}) as Record<string, unknown>;
        return [
          [res.save, 'download', many(r) ? uiFormat('Скачать № {0}', (Number((r as { pick?: unknown }).pick) || 0) + 1) : uiText('Скачать')],
          [res.more, 'refresh', uiText('Ещё вариант')],
        ].filter(([attr]) => !!attr);
      },
      steps: () => steps(),
      snap: () => snapOf(),
      price: () => ctx()?.svc.derive().price ?? 0,
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти им не нужна */
      missing: () => {
        const c = ctx();
        if (c) {
          const { s, d } = live(c);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
      T: {
        ...T,
        runH: (r: BotRunRun) => (kind === 'photo' ? (many(r) ? uiFormat('Создаём {0} фото', r.snap.n) : uiText('Создаём фото')) : uiText('Создаём видео')),
        runP: () => uiText(kind === 'photo' ? 'Можно уйти с экрана: бот пришлёт готовые фото в чат.' : 'Можно уйти с экрана: бот пришлёт ролик в чат.'),
        doneH: (r: BotRunRun) => uiText(kind === 'photo' ? (many(r) ? 'Фото готовы' : 'Фото готово') : 'Видео готово'),
        doneP: (r: BotRunRun) =>
          doneP(
            ctx()?.svc.get().order,
            esc(uiText(many(r) ? 'Бот уже прислал их в чат, в отдельную тему.' : 'Бот уже прислал его в чат, в отдельную тему.')),
            'pf',
          ),
        modStep: () => uiText('Не прошло проверку'),
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) => {
        const at = inst as unknown as Record<string, (...a: unknown[]) => unknown> & BotRunApi;
        const call = () => at[key](...args);
        /* итог фото: число кадров — по заказу сервера, до того как BotRun нарисует заголовок, сетку и «Скачать № N» */
        if (key === 'finish' && args[0] === 'done' && at.run) {
          const n = doneImages(ctx()?.svc.get().order);
          if (n) at.run.snap.n = n;
        }
        if (key === 'start' || key === 'retry') {
          const out = call();
          if (at.run) startedAt = Date.now();
          return out;
        }
        /* итог закрывают (уход с экрана) или к форме уходят, пока страница заказа ещё встаёт: после смены, тем же экземпляром */
        const wait = startedAt + SWAP_GUARD_MS - Date.now();
        if ((key === 'drop' || key === 'exit') && wait > 0) {
          const r = at.run;
          window.setTimeout(() => {
            if (at.run !== r || (key === 'drop' && h.mounted())) return;
            call();
          }, wait);
          return undefined;
        }
        return call();
      };
    }
  }
  function tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi {
    tailOpts = { create, opts };
    rebuild();
    return facade;
  }
  /** «Что дальше» итога: «Оживить фото» и «Сделать рилс» (кадры итога в «Видео» и «Рилс»), «Сделать кружок» и «Добавить речь» (ролик
      в «Говорящий кружок» по маршруту сервера): владелец и свежий заказ сверяются заново (result-handoff), заказ просмотрен */
  function nextAction(a: string, btn: HTMLElement) {
    const c = ctx();
    const s = c?.svc.get();
    const order = s?.order;
    const r = h.tail.run;
    if (!c || !s || !order || order.status !== 'done' || !r || r.st !== 'done' || c.moving) return;
    c.moving = true;
    markSeen(order.id);
    const ownerId = s.identity?.owner;
    const job = async () => {
      if (kind === 'photo') {
        const loaded = c.gallery.items(order);
        const items = loaded.length ? loaded : ((await c.gallery.load(order)) ?? []);
        if (!items.length) throw new Error('result_unavailable');
        const blobs = (index: number) => items.find(item => item.index === index)?.blob;
        if (a === 'anim') {
          const pick = items[Math.min(Math.max(0, Number((r as { pick?: unknown }).pick) || 0), items.length - 1)];
          await handoffPhoto({ ownerId, order, index: pick.index, destination: 'video', blobs, open: { screen: 'video', options: { mode: 'ph', sty: 'none' } } });
        } else
          await handoffPhoto({
            ownerId,
            order,
            index: items[0].index,
            extra: items.map(item => item.index),
            destination: 'reels',
            blobs,
            open: { screen: 'reels', options: { preset: 'people' } },
          });
        return;
      }
      const speech = a === 'circle' ? 'original' : 'text';
      await handoffVideo({ ownerId, order, speech, target: signal => videoResultTarget(speech, signal) });
    };
    void busyButton(btn, job)
      .catch(() => {
        if (btn.isConnected)
          tip(btn, kind === 'photo' ? 'Не удалось передать выбранное фото. Попробуйте снова.' : 'Не удалось передать видео. Откройте исходный заказ и попробуйте снова.');
      })
      .finally(() => {
        c.moving = false;
      });
  }

  const server: PhotoServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const c = ctx();
          if (!c || recovery(c)) return Infinity;
          const s = c.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, kind === 'photo' || !!h.durOf());
        },
      }),
    entry() {
      const f = cur();
      if (!f) return {};
      const o = entryFor(f, ctxOf(f));
      f.entryUsed = JSON.stringify(o);
      return o;
    },
    open(o) {
      const f = cur();
      if (!f) {
        h.setup(o);
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: файлы, стиль, люди и хвост прежнего сюда не переходят */
        h.camStop();
        h.VT.close();
        h.sheetClose();
        S.key = null;
        S.people = [];
        S.cheap = false;
        rebuild();
        painted = f;
        f.layout = '';
      }
      f.formShown = false;
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      h.setup(o);
      h.P.prune();
      if (o.meIn) h.P.setIn(true);
      const c = ctxOf(f);
      c.peek = peekIntent(c.app.id);
      if (c.driver.first(c.peek) === 'form' && root) root.scrollTop = 0;
      f.layout = layoutOf();
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      for (const c of f.ctxs.values()) {
        c.consent?.close();
        c.consent = null;
        c.result.stop();
      }
    },
    tail,
    wrap<T>(slot: string, value: T, extra?: unknown): T {
      const f = cur(),
        c = f ? ctxOf(f) : null,
        form = formOf(c);
      const values = (key: string) => studioFields(form).find(field => field.key === key)?.values ?? null;
      if (slot === 'cheap') {
        const cheap = value as unknown as { price?: unknown; go?: unknown; where?: unknown };
        /* скидку «Сделать дешевле» публикует форма (Q5): без неё у подвала нет ни цены, ни действия скидки. Форма — текущего режима
           в момент вопроса: подвал оборачивается один раз при make(K), когда кадра ещё нет */
        const offer = () => {
          const now = ctx();
          return now ? studioCheapOfferAt(formOf(now), basePrice(kind === 'photo' ? S.n : h.durOf() > 5 ? 10 : 5)) : null;
        };
        return {
          where: cheap.where,
          get price() {
            return offer() ? cheap.price : undefined;
          },
          get go() {
            return offer() ? cheap.go : undefined;
          },
          get percent() {
            return offer()?.percent;
          },
        } as unknown as T;
      }
      if (slot === 'modes') {
        const list = tools(f);
        if (!list.length) return value;
        const file = kind === 'photo' ? ['style', 'edit'] : ['style', 'photo', 'edit'];
        const byText = kind === 'photo' ? 'generate' : 'text';
        return (value as unknown as Array<[string, string, string]>).filter(([m]) =>
          m === 'tx' ? list.some(tool => tool.mode === byText) : list.some(tool => file.includes(tool.mode)),
        ) as unknown as T;
      }
      if (slot === 'arOpts' || slot === 'nOpts' || slot === 'durOpts') {
        const key = slot === 'arOpts' ? 'format' : slot === 'nOpts' ? 'count' : 'duration';
        const published = values(key);
        const all = value as unknown as Array<[string, ...unknown[]]>;
        const opts = published?.length ? all.filter(([v]) => published.includes(slot === 'arOpts' && v === 'src' ? sourceFormat(kind) : String(v))) : all;
        return optsT(opts.length ? opts : all) as unknown as T;
      }
      if (slot === 'items') return itemsT(value as unknown as Array<Record<string, unknown>>) as unknown as T;
      if (slot === 'box') {
        /* поле описания (BotCore.html.box): подпись, подсказка, подпись поля, чипы — словарём профиля, свои строки поля — шов tx */
        const box = value as unknown as Record<string, unknown>;
        return {
          ...box,
          tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
          ph: tr(box.ph),
          aria: tr(box.aria),
          label: tr(box.label),
          chips: Array.isArray(box.chips) ? (box.chips as Array<Record<string, unknown>>).map(chip => ({ ...chip, l: tr(chip.l) })) : box.chips,
        } as unknown as T;
      }
      if (slot === 'next' && kind === 'photo')
        return (value as unknown as Array<[string, string, string]>).map(([attr, icon, label]) => [attr, icon, uiText(label)]) as unknown as T;
      if (slot === 'next') {
        /* «Сделать кружок» и «Добавить речь» — только по маршруту, который публикует сервер (result_actions документа экрана «Видео»);
           кружок — не у «Объёмного фото» и не у ролика без звука (circleBlocked родного итога) */
        if (kind !== 'video' || !f || !c) return value;
        const actions = record(record(f.doc.data()).video).result_actions;
        const inputs = sentInputs(c) ?? c.svc.get().inputs;
        const circle = !!videoActionId(actions, 'original') && inputs.audio !== 'false' && modeNow(f, c) !== 'parallax';
        const speech = !!videoActionId(actions, 'text');
        return (value as unknown as Array<[string, string, string]>)
          .filter(([attr]) => (attr === 'data-pr-circle' ? circle : attr === 'data-pr-voice' ? speech : true))
          .map(([attr, icon, label]) => [attr, icon, uiText(label)]) as unknown as T;
      }
      void extra;
      return value;
    },
    has(cap) {
      const f = cur(),
        c = f ? ctxOf(f) : null,
        form = formOf(c);
      if (!f || !c || !form) return cap === 'none' || cap === 'reference' ? true : false;
      const policy = record(form.constraints?.[kind]);
      if (cap === 'improve')
        return kind === 'photo' ? policy.improvement_available === true : record(policy.prompt_improvement).available === true;
      if (cap === 'people') return policy.people_available === true && Number(policy.max_people) > 0;
      if (cap === 'reference') return c.ref.has() && policy.reference_available === true;
      if (cap === 'shoot') {
        if (shootAsk) return true;
        shootAsk = true;
        try {
          return c.cam.has();
        } finally {
          shootAsk = false;
        }
      }
      /* «Без стиля»: у «По описанию» — значение none поля стиля; у режимов из файла — режим без стиля (edit у фото, photo и edit у видео) */
      const style = studioFields(form).find(field => field.key === 'style');
      if (S.mode === 'tx') return !!style?.values.includes('none');
      const list = tools(f);
      if (!list.length) return !!style?.values.includes('none');
      return kind === 'photo' ? list.some(tool => tool.mode === 'edit') : list.some(tool => tool.mode === 'photo' || tool.mode === 'edit');
    },
    peopleMax() {
      const policy = record(formOf(ctx())?.constraints?.[kind]);
      const max = Number(policy.max_people);
      return Number.isSafeInteger(max) && max > 0 ? Math.min(4, max) : K.people.max;
    },
    styles,
    themes() {
      const f = cur(),
        c = f ? ctxOf(f) : null;
      if (!f || !c || S.app || S.preset || isTemplate(f, c)) return [];
      const list = styles();
      const policy = { mode: (kind === 'photo' ? modeNow(f, c) : 'generate') as never, styles: list.map(s => ({ themes: s.th })) as never };
      return photoThemes(policy, photoThemeOrder(f.doc.data(), kind));
    },
    pic(s, add) {
      if (!s || s.none) return '';
      if (s.cov) {
        /* движение товара (V148): кадр своего главного фото со знаком; своего фото нет — превью сервера или силуэт (PM6) */
        const own = S.files[0] ?? S.file;
        const base = own && !own.demo ? own.url : (s.img ?? SILHOUETTE);
        return h.coverPic(s, { sign: !add, f: { url: base } });
      }
      return s.img ? img(s.img) : '';
    },
    fileUrl: () => (h.src() === 'vd' ? (S.clip?.poster ?? SILHOUETTE) : (S.file?.url ?? SILHOUETTE)),
    sample: () => SILHOUETTE,
    example() {
      if (!h.fixed()) return null;
      const policy = record(formOf(ctx())?.constraints?.[kind]);
      const raw = record(policy.example);
      const ex = uploadExample(raw);
      if (!ex) return null;
      return {
        before: { src: ex.before, f: '' },
        after: { src: ex.after, f: '' },
        mode: raw.mode === 'motion' ? 'motion' : '',
        /* подписи примера — словарём профиля (сервер публикует русский шаблон) */
        title: ex.title ? uiText(ex.title) : '',
        text: ex.text ? uiText(ex.text) : '',
        ...(ex.labels ? { labels: ex.labels.map(label => uiText(label)) } : {}),
      };
    },
    examples: s => (s.ex.length ? s.ex : s.img ? [s.img] : []).slice(0, 4).map(url => img(url)),
    styleImg: s => s.img,
    title() {
      const f = cur(),
        c = f ? ctxOf(f) : null;
      if (!f || !c) return '';
      if (isTemplate(f, c)) return c.app.name;
      const mode = modeNow(f, c);
      const shared = kind === 'photo' ? ['generate', 'style', 'edit'] : ['text', 'style', 'photo', 'edit'];
      return mode && !shared.includes(mode) ? c.app.name : '';
    },
    price(units, cheap) {
      const base = basePrice(units);
      if (cheap) {
        const offer = studioCheapOfferAt(formOf(ctx()), base);
        if (offer) return offer.price;
      }
      return base;
    },
    unit() {
      const c = ctx();
      const form = formOf(c);
      const amount = c ? (cardUnit(c, form) ?? (form ? (kind === 'photo' ? form.expected_price : Math.round(form.expected_price / 5)) : null)) : null;
      if (amount === null) return '';
      const f = cur();
      /* шаблон каталога «Видео» с ценой за запуск — без «за секунду» (как StudioUnitPrice total родной формы) */
      const total = kind === 'video' && !!f && !!c && isTemplate(f, c) && priceUnit(c.app.price) !== 'per_second';
      return core().html.unit(amount, total ? '' : uiText(K.unit));
    },
    missing,
    ready,
    missText(kind2) {
      const c = ctx();
      const s = c?.svc.get();
      switch (kind2) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText(s?.error || (kind === 'video' ? 'Этот режим видео пока недоступен' : 'Этот инструмент сейчас недоступен. Попробуйте обновить форму.'));
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'upload':
          return uiText('Загружаем файл');
        case 'upfail':
          return uiText(kind === 'video' && S.clip ? 'Загрузите видео заново' : 'Загрузите фото заново');
        case 'rule':
          return uiText(rule);
        /* ключи missing() шаблона */
        case 'crop':
          return uiText('Выберите кадр');
        case 'file':
          return uiText(h.mediaOn() ? 'Загрузите фото или видео' : 'Загрузите фото');
        case 'long':
          return uiFormat('Описание длиннее {0} знаков', limit.max);
        case 'text':
          return uiFormat('Добавьте описание, от {0} знаков', limit.min);
        case 'me':
          return uiText('Добавьте свои фото в «Кто в кадре»');
        case 'person':
          return uiText('Добавьте фото людям в кадре');
        case 'ref':
          return uiText(h.refNow()?.miss() || 'Проверяем картинку');
        default:
          return '';
      }
    },
    label() {
      const c = ctx();
      if (!c) return null;
      const { s, d } = live(c);
      if (s.intent && !d.orderId) return uiText('Продолжить проверку');
      if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
      if (c.media.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
      if (rightsNeeded(c)) return uiText(RIGHTS);
      /* «Создать фото · 30 C», «Создать 2 фото · 60 C», «Создать видео · от 60 C» (FOOT label шаблона) — словарём профиля */
      const what = kind === 'photo' ? (S.n > 1 ? uiFormat('Создать {0} фото', S.n) : uiText('Создать фото')) : uiText('Создать видео');
      const price = server.price(kind === 'photo' ? S.n : h.durOf() > 5 ? 10 : 5, S.cheap);
      return uiFormat(h.priceFrom() ? '{0} · от {1} C' : '{0} · {1} C', what, core().cNum(price));
    },
    upOpts(w) {
      const photo = acceptString({ kind: 'photo', accept: PHOTO_TYPES });
      const video = acceptString({ kind: 'video', accept: VIDEO_TYPES });
      const note = () => {
        if (w === 'media') return '';
        const policy = mediaPolicy(formOf(ctx()), 'photo');
        return policy ? mediaNote(policy.accept, policy.maxBytes) : '';
      };
      return {
        accept: w === 'media' ? `${photo},${video}` : photo,
        mb: w === 'media' ? 200 : 10,
        note,
        tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
      };
    },
    refOpts: () => ({
      upload: (file: File, e: Parameters<ReturnType<RefBridge['opts']>['upload']>[1]) => {
        const c = ctx();
        return c ? c.ref.opts().upload(file, e) : Promise.resolve({});
      },
      remove: (e: Parameters<ReturnType<RefBridge['opts']>['remove']>[0]) => {
        ctx()?.ref.opts().remove(e);
      },
    }),
    pick(file) {
      const f = cur();
      if (!f || !onForm()) return;
      const c = ctxOf(f);
      if (h.trayOn()) {
        server.pickTray([file]);
        return;
      }
      const video = kind === 'video' && h.mediaOn() && isVideoFile(file);
      const key = video ? 'video' : 'photo';
      if (c.svc.derive().locked || f.switching) {
        h.UP.say(uiText(lockText(c)));
        return;
      }
      /* файл другого вида (ролик ↔ фото) может требовать другой режим: сначала режим, затем загрузка в его сервис */
      const target = wanted(f, c, { ...viewOf(), src: video ? 'vd' : 'ph' });
      if (target !== null && !c.media.pending()) {
        void switchTo(f, c, target).then(ok => {
          if (ok && frame === f) void ctxOf(f).media.pick(key, file);
        });
        return;
      }
      void c.media.pick(key, file);
    },
    pickTray(files, at) {
      const c = ctx();
      if (!c || !onForm()) return;
      /* рамка лотка отдаёт номер заменяемого фото или ничего (null, '') — тогда в свободные слоты */
      const slot = at === undefined || at === null || String(at) === '' || !Number.isInteger(Number(at)) ? undefined : Number(at);
      void c.media.pickSlots(files, trayKeys(c), slot);
    },
    remove(w, i) {
      const c = ctx();
      if (!c) return true;
      if (c.svc.derive().locked) {
        h.UP.say(uiText(lockText(c)));
        return false;
      }
      if (w === 'tray') {
        c.media.removeSlot(i ?? 0, trayKeys(c));
        return true;
      }
      const key = w === 'media' && S.clip ? 'video' : 'photo';
      c.shown.delete(key);
      c.media.remove(key);
      return true;
    },
    recFailed: () => h.UP.say(uiText('Запись недоступна. Загрузите видео.')),
    camFailed: () => h.UP.say(uiText('Камера недоступна. Загрузите файл.')),
    push,
    mode(_m, btn) {
      const f = cur(),
        c = f ? ctxOf(f) : null;
      if (!f || !c) return false;
      if (f.switching || c.svc.derive().locked || c.media.pending()) {
        core().shake(btn, core().offBuzz);
        tip(btn, f.switching ? 'Проверяем доступность' : lockText(c));
        return false;
      }
      return true;
    },
    styled() {
      /* смену режима по стилю ведёт push (syncFoot шаблона идёт раньше этого вызова) */
    },
    start(btn) {
      const c = ctx();
      if (!c) return;
      const { s, d } = live(c);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(c);
      if (fix) {
        fix(btn);
        return;
      }
      if (h.FOOT.poor()) {
        h.FOOT.poorOpen();
        return;
      }
      h.tail.start();
    },
    steps,
    snap: snapOf,
    resPic(sn, i) {
      if (kind !== 'photo') return pictureOf(sn);
      const c = ctx();
      const order = c?.svc.get().order;
      const item = c && order ? c.gallery.items(order)[i] : undefined;
      return item ? `<img src="${h.esc(item.url)}" alt="">` : pictureOf(sn);
    },
    runPic: sn => pictureOf(sn),
    click(d, el) {
      const c = ctx();
      if (d.pfChat !== undefined) {
        chatClick({
          order: c?.svc.get().order,
          el,
          text: kind === 'photo' ? 'Тут откроется тема чата, куда бот прислал фото' : 'Тут откроется тема чата, куда бот прислал ролик',
          tip: (at, value) => h.ui().tip(at, value),
        });
        return true;
      }
      if (d.pfSettings !== undefined) {
        openSettings();
        return true;
      }
      if (d.pfRetry === undefined && d.pfRchk === undefined && d.pfFix === undefined) return false;
      if (!c || c.driver.busy()) return true;
      if (d.pfRetry !== undefined) h.tail.retry();
      else if (d.pfRchk !== undefined) void c.driver.check(el);
      else void c.driver.leave(() => h.tail.exit(() => h.paintPage()), false, fileKey() ?? 'photo');
      return true;
    },
    play(btn) {
      if (kind !== 'video') return false;
      const c = ctx();
      const r = h.tail.run;
      const order = c?.svc.get().order;
      if (!c || !r || r.st !== 'done') return true;
      const asset = mp4Of(order);
      if (!asset) {
        tip(btn, 'Видео ещё загружается');
        return true;
      }
      void c.result
        .toggle(asset.index, { el: btn, kind: 'frame', dur: Number(r.snap.dur) || null, poster: record(record(record(order).display).picture).url })
        .then(ok => {
          if (!ok && btn.isConnected) tip(btn, c.svc.get().error || 'Видео ещё загружается');
        });
      return true;
    },
    async download(i) {
      const c = ctx();
      const order = c?.svc.get().order;
      if (!c || !order || order.status !== 'done') throw new Error('result_unavailable');
      if (kind === 'photo') {
        const loaded = c.gallery.items(order);
        const items = loaded.length ? loaded : ((await c.gallery.load(order)) ?? []);
        const item = items[i] ?? items[0];
        if (!item) throw new Error('result_unavailable');
        return { data: item.blob, name: item.name || `steos-photo-${i + 1}.jpg` };
      }
      const asset = mp4Of(order);
      if (!asset) throw new Error('result_unavailable');
      return c.result.download(asset.index);
    },
    next: (a, btn) => nextAction(a, btn),
    leave(go) {
      const c = ctx();
      if (!c) return;
      c.result.stop();
      /* «Ещё вариант», «Заменить фото / видео», «Изменить описание»: заказ просмотрен, к форме с теми же настройками, файл остаётся (шаблон) */
      void c.driver.leave(go, false, fileKey() ?? 'photo');
    },

  };
  /* ---- смена языка профиля (steos:locale): хвост — TAIL.show с подписями этапов нового языка, форма — paintPage ---- */
  document.addEventListener('steos:locale', () => {
    const r = h.tail.run;
    if (r) {
      const fresh = server.steps() ?? [];
      for (const step of r.steps) {
        const same = fresh.find(item => item[0] === step[0]);
        if (same) {
          step[2] = same[2];
          step[3] = same[3];
        }
      }
    }
    const f = cur();
    const root = h.root();
    if (!f || !root || !h.mounted()) return;
    if (r) h.tail.show(root);
    else h.paintPage();
    changed(f);
  });
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.FOOT.sync();
  });

  instances[kind] = { bind };
  return server;
}

/** ENGINES.photo/video.bind: привязка кадра к серверу вида, созданному движком (без движка — пустая привязка) */
export function bindPhotoEngine(kind: PhotoKind) {
  return (b: EngineBinding): EngineBound => instances[kind]?.bind(b) ?? { changed() {}, release() {} };
}

/** Экраны «photo» и «video» реестра движков: движки шаблона window.BotPhoto / BotVideo, атрибуты data-pf-* (одна разметка, id по NS),
    без правой панели, знак заказа своего вида, режимы — приложения сервера (mode-group) */
export const PHOTO_ENGINE: EngineScreen = { global: 'BotPhoto', prefix: 'pf', side: false, kind: 'photo', modes: true, bind: bindPhotoEngine('photo') };
export const VIDEO_ENGINE: EngineScreen = { global: 'BotVideo', prefix: 'pf', side: false, kind: 'video', modes: true, bind: bindPhotoEngine('video') };

export function registerPhotoEngines() {
  ENGINES.photo = PHOTO_ENGINE;
  ENGINES.video = VIDEO_ENGINE;
}
